import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { DoneAll, Done, Close as CloseIcon } from "@mui/icons-material";
import moment from "moment";
import { IMAGE_URL } from "src/network/endpoints";
import { useSocketContext } from "src/context/SocketContext";

/**
 * MessageInfoDialog Component
 * 
 * Displays message read and delivery status information.
 * Fetches message info from backend via socket when dialog opens.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Object} props.message - The message object to show info for
 */
const MessageInfoDialog = ({ open, onClose, message }) => {
  const { emit, addEventListener, isConnected } = useSocketContext();
  
  // State to store message info data from socket
  const [messageInfo, setMessageInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Ref to track if we've already requested info for this message
  const requestedMessageIdRef = useRef(null);

  // Request message info when dialog opens
  useEffect(() => {
    if (open && message && isConnected) {
      const messageId = message._id || message.id;
      
      // Only request if we haven't already requested for this message
      if (messageId && requestedMessageIdRef.current !== messageId) {
        setIsLoading(true);
        setMessageInfo(null);
        requestedMessageIdRef.current = messageId;
        
        // Emit getMessageInfo socket event
        if (emit) {
          emit("getMessageInfo", { messageId });
        }
      }
    } else if (!open) {
      // Reset when dialog closes
      requestedMessageIdRef.current = null;
      setMessageInfo(null);
      setIsLoading(false);
    }
  }, [open, message, isConnected, emit]);

  // Listen for MessageInfo socket event
  useEffect(() => {
    if (!open || !isConnected) return;

    const handleMessageInfo = (data) => {
      console.log("MessageInfo received-------->", data);
      console.log("MessageInfo data structure:", JSON.stringify(data, null, 2));
      
      // Handle different data structures from backend
      // Backend sends: { message: populatedMessage }
      // The data might be the message directly or wrapped in a message property
      let receivedMessage = null;
      let receivedMessageId = null;
      
      if (data?.message) {
        // Data is wrapped: { message: {...} }
        receivedMessage = data.message;
        receivedMessageId = receivedMessage._id || receivedMessage.id;
      } else if (data?._id || data?.id) {
        // Data is the message directly
        receivedMessage = data;
        receivedMessageId = data._id || data.id;
      }
      
      const currentMessageId = message?._id || message?.id;
      
      console.log("Received message ID:", receivedMessageId);
      console.log("Current message ID:", currentMessageId);
      
      // If we have message data, use it (even if IDs don't match, in case backend sends different format)
      if (receivedMessage) {
        // Check if IDs match, or if we don't have a current message ID (first load)
        if (!currentMessageId || (receivedMessageId && String(receivedMessageId) === String(currentMessageId))) {
          setIsLoading(false);
          setMessageInfo(receivedMessage);
          console.log("MessageInfo set successfully:", receivedMessage);
        } else {
          console.log("Message ID mismatch - ignoring");
        }
      } else {
        console.log("No message data found in MessageInfo response");
      }
    };

    // Add event listener
    const unsubscribe = addEventListener("MessageInfo", handleMessageInfo);

    // Cleanup on unmount or when dialog closes
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [open, isConnected, addEventListener, message]);

  // Use messageInfo if available, otherwise fall back to message prop
  const displayMessage = messageInfo || message;
  
  // Extract readBy and deliveredTo from the message
  const readBy = displayMessage?.readBy || [];
  const deliveredTo = displayMessage?.deliveredTo || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          message info
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* message preview section */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#333",
              fontSize: "0.95rem",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {displayMessage?.text || displayMessage?.message || "media message"}
          </Typography>
          {displayMessage?.attachments && displayMessage.attachments.length > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "#666", mt: 0.5, display: "block" }}
            >
              {displayMessage.attachments.length} attachment(s)
            </Typography>
          )}
        </Box>

        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              color: "#999",
            }}
          >
            <Typography variant="body2">Loading message info...</Typography>
          </Box>
        )}

        {/* read by section - only show if there are users who read the message */}
        {!isLoading && readBy && readBy.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1.5,
                gap: 1,
              }}
            >
              <DoneAll sx={{ fontSize: 20, color: "#4FC3F7" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#333" }}
              >
                read by
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {readBy.map((readInfo, index) => {
                // handle both object format {userId: {}, timestamp: ""} and simple userId format
                const user = readInfo?.userId || readInfo;
                const timestamp = readInfo?.timestamp || readInfo?.readAt || readInfo?.readAtTimestamp;
                const userName =
                  user?.fullName ||
                  user?.userName ||
                  user?.name ||
                  user?.userId?.fullName ||
                  user?.userId?.userName ||
                  user?.userId?.name ||
                  "unknown user";
                const userImage = 
                  user?.image?.profilePic || 
                  user?.profileImage ||
                  user?.userId?.image?.profilePic ||
                  user?.userId?.profileImage;

                return (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        src={userImage ? `${IMAGE_URL}${userImage}` : undefined}
                        alt={userName}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "#7C3AED",
                          fontSize: "1rem",
                        }}
                      >
                        {userName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#333" }}
                        >
                          {userName}
                        </Typography>
                        {timestamp && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", fontSize: "0.75rem" }}
                          >
                            {moment(timestamp).format("MMM DD, YYYY [at] h:mm A")}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                    {index < readBy.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}

        {/* delivered to section - only show if there are users who received but haven't read */}
        {!isLoading && deliveredTo && deliveredTo.length > 0 && (
          <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1.5,
                gap: 1,
              }}
            >
              <Done sx={{ fontSize: 20, color: "#9E9E9E" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#333" }}
              >
                delivered to
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {deliveredTo
                .filter((deliveryInfo) => {
                  // filter out users who have already read the message
                  const deliveredUserId =
                    deliveryInfo?.userId?._id ||
                    deliveryInfo?.userId ||
                    deliveryInfo?._id;
                  const hasRead = readBy?.some((readInfo) => {
                    const readUserId =
                      readInfo?.userId?._id ||
                      readInfo?.userId ||
                      readInfo?._id;
                    return String(readUserId) === String(deliveredUserId);
                  });
                  return !hasRead;
                })
                .map((deliveryInfo, index, filteredArray) => {
                  // handle both object format {userId: {}, timestamp: ""} and simple userId format
                  const user = deliveryInfo?.userId || deliveryInfo;
                  const timestamp =
                    deliveryInfo?.timestamp || 
                    deliveryInfo?.deliveredAt || 
                    deliveryInfo?.deliveredAtTimestamp;
                  const userName =
                    user?.fullName ||
                    user?.userName ||
                    user?.name ||
                    user?.userId?.fullName ||
                    user?.userId?.userName ||
                    user?.userId?.name ||
                    "unknown user";
                  const userImage =
                    user?.image?.profilePic || 
                    user?.profileImage ||
                    user?.userId?.image?.profilePic ||
                    user?.userId?.profileImage;

                  return (
                    <React.Fragment key={index}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Avatar
                          src={
                            userImage ? `${IMAGE_URL}${userImage}` : undefined
                          }
                          alt={userName}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "#9E9E9E",
                            fontSize: "1rem",
                          }}
                        >
                          {userName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {userName}
                          </Typography>
                          {timestamp && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#666", fontSize: "0.75rem" }}
                            >
                              {moment(timestamp).format(
                                "MMM DD, YYYY [at] h:mm A"
                              )}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                      {index < filteredArray.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
            </List>
          </Box>
        )}

        {/* no status available message */}
        {!isLoading &&
          (!readBy || readBy.length === 0) &&
          (!deliveredTo || deliveredTo.length === 0) && (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                color: "#999",
              }}
            >
              <Typography variant="body2">
                no delivery or read information available
              </Typography>
            </Box>
          )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageInfoDialog;
